import React from "react";
import {
    ConfirmDialog,
    FormValidationSummary,
    ShimmeredDetailsList,
    useNotifications,
    WarningMessage
} from "sprint-common-web";

import { FileRecordStatus } from "../../../../../../common/types/FileRecordStatus";
import { useUpdateFileRecordsStatusDialog } from "../hooks/useUpdateFileRecordsStatusDialog";
import { createUpdateFileRecordStatusColumns } from "../utils/createUpdateFileRecordStatusColumns";

interface UpdateMultipleFileRecordsStatusDialogProps {
    isOpen: boolean;
    onClose(): void;
    selectedStatus: FileRecordStatus;
}

export const UpdateMultipleFileRecordsStatusDialog: React.VFC<UpdateMultipleFileRecordsStatusDialogProps> = ({
    isOpen,
    onClose,
    selectedStatus
}) => {
    const { success, error } = useNotifications();

    const {
        items,
        performAllItems,
        isActionInProgress,
        handleUpdateFileRecordStatus,
        validationResult,
        statusName
    } = useUpdateFileRecordsStatusDialog(selectedStatus);

    const handleConfirm = async () => {
        const results = await performAllItems(async (item) => {
            await handleUpdateFileRecordStatus(item.id);
        });

        if (results.every(({ status }) => status === "fulfilled")) {
            onClose();

            success(`Selected file records changed to ${statusName}`);
        } else error(`Changing to ${statusName} failed`);
    };

    const columns = createUpdateFileRecordStatusColumns();

    return (
        <ConfirmDialog
            title={`Change status to ${statusName}`}
            text={`Are you sure you want to change given file records to ${statusName}?`}
            hidden={!isOpen}
            isBlocking={isActionInProgress}
            width={"65%"}
            contentHeight={500}
            confirm={{ onClick: handleConfirm, disabled: !items.length }}
            cancel={{ onClick: onClose }}
        >
            <FormValidationSummary validationResult={validationResult} />
            {selectedStatus === FileRecordStatus.Destroyed && (
                <WarningMessage message="Destroying these file records is irreversible. All associated assets will be deprecated. Please confirm you want to proceed." />
            )}
            {!!items.length && <ShimmeredDetailsList columns={columns} items={items} layoutMode="justified" />}
        </ConfirmDialog>
    );
};
